









import type { PropType } from '@nuxtjs/composition-api';
// eslint-disable-next-line no-duplicate-imports
import { computed, defineComponent, provide } from '@nuxtjs/composition-api';

import { useScreenSize } from '~/diptyqueTheme/composable';
import type {
  CollectionPushModel,
  FlexibleSectionModel,
  ProductsGroupModel
} from '~/diptyqueTheme/types/contentful';
// eslint-disable-next-line no-duplicate-imports
import {
  ProductsLayout,
  VisualPosition
} from '~/diptyqueTheme/types/contentful';
import { DESC_SETTING } from '~/diptyqueTheme/types/contentful/constants/injectionKeys.const';

export default defineComponent({
  name: 'CollectionPush',
  components: {
    DesktopFullWidth: () => import('./declinations/DesktopFullWidth.vue'),
    DesktopSide: () => import('./declinations/DesktopSide.vue')
  },
  props: {
    sectionData: {
      required: true,
      type: Object as PropType<FlexibleSectionModel | CollectionPushModel>
    }
  },

  setup(props) {
    const { isDesktop } = useScreenSize();

    const visualPositionMap = {
      'Option 1 - Slider below': VisualPosition.TOP,
      'Option 2 - Slider on the right': VisualPosition.LEFT,
      'Option 3 - Slider on the left': VisualPosition.RIGHT
    };

    /**
     * Maps the ProductsLayout enum to the corresponding layout type.
     * This is used to determine how products should be displayed based on the layout setting.
     */
    const layoutsMap = {
      [ProductsLayout.DEFAULT]: 'slider',
      [ProductsLayout.CAROUSEL]: 'slider',
      [ProductsLayout.GRID]: 'grid'
    };

    /**
     * Maps the visual position to the corresponding component name.
     * This is used to dynamically select the component based on the visual position of the section.
     */
    const componentsMap = {
      [VisualPosition.DEFAULT]: 'DesktopFullWidth',
      [VisualPosition.TOP]: 'DesktopFullWidth',
      [VisualPosition.LEFT]: 'DesktopSide',
      [VisualPosition.RIGHT]: 'DesktopSide'
    };

    const componentName = computed<string>(() => {
      const position = adaptedData.value?.visual?.posD;

      return componentsMap[position] || 'DesktopFullWidth';
    });

    const adaptedData = computed<FlexibleSectionModel>(() => {
      if (props.sectionData.__typename === 'Flexible') {
        return props.sectionData as FlexibleSectionModel;
      }

      const collectionPushData = props.sectionData as CollectionPushModel;

      return {
        ...props.sectionData,
        eTitle: collectionPushData.etitle,
        ...(collectionPushData.button && {
          buttonCollection: { items: [collectionPushData.button] }
        }),
        ...(collectionPushData.productGroup && {
          itemsCollection: { items: [collectionPushData.productGroup] }
        }),
        visual: {
          vis: collectionPushData.visual,
          visD: collectionPushData.visualDesktop,
          pos: VisualPosition.DEFAULT,
          posD: collectionPushData.dVar
            ? visualPositionMap[collectionPushData.dVar]
            : VisualPosition.DEFAULT,
          vidSet: collectionPushData.vSettings
        },
        productDescriptionSettings: collectionPushData.descrSettings
      } as FlexibleSectionModel;
    });

    const descriptionData =
      (props.sectionData as CollectionPushModel).descrSettings ||
      (props.sectionData as FlexibleSectionModel).productDescriptionSettings ||
      [];
    provide(DESC_SETTING, descriptionData);

    const productsData = computed(() => {
      const item: ProductsGroupModel =
        adaptedData.value?.itemsCollection?.items[0] ||
        ({} as ProductsGroupModel);

      return {
        listOfSkus: item.sku || [],
        layout: isDesktop.value
          ? 'slider'
          : layoutsMap[item.layout] || 'slider',
        altVisuals: item?.altvisCollection?.items || []
      };
    });

    return {
      componentName,
      productsData,
      adaptedData
    };
  }
});
